import classNames from 'classnames';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { NotebookIcons, VideoIcons } from '../../../../../../../../icons/icons';
import { Button } from '../../../../../../../../ui';
import { CustomSelect } from '../../../../../../../../ui/custom_react_select/custom_select';
import { ClockIcon } from '../../../../../../../../ui/icons/icons';
import { TextArea } from '../../../../../../../../ui/input/text_area/text_area';
import {
  getAssessmentSettings,
  updateAnswerNotesAndStatus,
} from '../../../../../../../../utils/api_service/assessment_api';
import { getVideoFromStorage } from '../../../../../../../../utils/api_service/storage_api';

import { Permission } from '../../../../../../../../utils/helpers/permissions';
import PermissionGate from '../../../../../../../../utils/hooks/PermissionGate';
import {
  AssessmentQuestionResult,
  FacialEmotions,
} from '../../../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../../../utils/types/react_select_types';

interface SkillQuestionAndAnswerProps {
  assessmentQuestionResult: AssessmentQuestionResult;
  isVideoBlur?: boolean;
  updateDetailedQuestionArray: (
    questionArray: AssessmentQuestionResult
  ) => void;
  setResultOpen: (value: boolean) => void;
  showSystemAnswer?: boolean;
  showVideo?: boolean;
}

export const SkillQuestionAndAnswer: React.FC<SkillQuestionAndAnswerProps> = ({
  assessmentQuestionResult,
  isVideoBlur = false,
  updateDetailedQuestionArray,
  setResultOpen,
  showSystemAnswer,
  showVideo,
}) => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [questionResult, setQuestionResult] =
    useState<AssessmentQuestionResult>(assessmentQuestionResult);
  const [blurVideo, setBlurVideo] = useState<boolean>(false);

  useEffect(() => {
    getAssessmentSettings(assessmentQuestionResult.assessmentId).then(
      (response) => {
        setBlurVideo(response.blurVideo);
      },
    );
  }, [assessmentQuestionResult.assessmentId]);

  const answerSelect: SelectOptions[] = [
    {
      value: 'correct',
      label: 'Answer is Correct',
    },
    {
      value: 'incorrect',
      label: 'Answer is In-Correct',
    },
  ];

  const getHighestFacialEmotionLabel = (
    facialEmotions: FacialEmotions,
  ): string => {
    let highestValue = 0; // Initialize highest value
    let highestLabel: string = ''; // Initialize highest label

    // Iterate over each key-value pair in the facialEmotions object
    Object.entries(facialEmotions).forEach(([key, value]) => {
      if (value !== undefined && value > highestValue) {
        // Check if the current value is higher than the highestValue
        highestValue = value; // Update highestValue
        highestLabel = key; // Update highestLabel
      }
    });

    return highestLabel; // Return the label with the highest value
  };

  const getHighestFacialEmotionPercentage = (
    facialEmotions: FacialEmotions,
  ): number => {
    let total = 0; // Initialize total sum of all values
    let highestValue = 0; // Initialize highest value

    // Calculate the total and find the highest value
    Object.values(facialEmotions).forEach((value) => {
      if (value !== undefined) {
        total += value; // Add each value to the total
        if (value > highestValue) {
          highestValue = value; // Update highestValue if current value is greater
        }
      }
    });

    // If total is 0 to avoid division by zero error
    if (total === 0) return 0;

    // Calculate the percentage of the highest value
    const highestPercentage = (highestValue / total) * 100;

    // Return the percentage as a number rounded to 2 decimal places
    return parseFloat(highestPercentage.toFixed(2));
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setQuestionResult({
      ...questionResult,
      personalNotes: event.target.value,
    });
  };

  const handleSelectChange = (option: SelectOptions) => {
    option.value === 'correct'
      ? setQuestionResult({
        ...questionResult,
        score: questionResult.maxScore,
        isCorrect: true,
      })
      : setQuestionResult({
        ...questionResult,
        score: 0,
        isCorrect: false,
      });
  };

  const saveUpdatedQuestionResult = () => {
    updateAnswerNotesAndStatus(
      questionResult.id,
      questionResult.isCorrect,
      questionResult.personalNotes,
    ).then((response) => {
      if (response.ok) {
        updateDetailedQuestionArray(questionResult);
        setResultOpen(false);
      }
    });
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  const VideoModal = () => {
    if (!isVideoModalOpen) return null;

    return (
      <div className='fixed inset-0 z-50 overflow-y-auto'>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          { /* Background overlay */ }
          <div
              className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity'
              onClick={handleCloseVideoModal}
          ></div>

          { /* Modal panel */ }
          <div className='inline-block align-bottom bg-white rounded-none px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6 border border-gray-300'>
            <div className='absolute top-0 right-0 pt-4 pr-4'>
              <button
                  type='button'
                  className='bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  onClick={handleCloseVideoModal}
              >
                <span className='sr-only'>Close</span>
                <svg
                    className='h-6 w-6'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            <div className='sm:flex sm:items-start'>
              <div className='w-full'>
                { /* Modal Header */ }
                <div className='flex items-center gap-3 mb-6'>
                  <div className='w-8 h-8 rounded-none bg-blue-50 border border-blue-100 flex items-center justify-center'>
                    <VideoIcons className='w-4 h-4 text-blue-600' />
                  </div>
                  <div>
                    <h3 className='text-lg font-medium text-gray-900'>
                      Video Response
                    </h3>
                    <p className='text-sm text-gray-500 mt-1'></p>
                  </div>
                </div>

                <div className='sm:grid sm:grid-cols-5 sm:gap-6'>
                  { /* Left Column: Video */ }
                  <div className='sm:col-span-3'>
                    { /* Video Player Container */ }
                    <div className='bg-gray-900 rounded-none overflow-hidden mb-4 sm:mb-0'>
                      <div className='aspect-video flex items-center justify-center h-full'>
                        { blurVideo ? (
                          <div className='flex flex-col items-center justify-center text-gray-400'>
                            <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-4'></div>
                            <p className='text-lg font-medium'>
                              Loading Video...
                            </p>
                            <p className='text-sm'>
                              Please wait while we fetch your video.
                            </p>
                          </div>
                        ) : questionResult.videoUrl ? (
                          <video
                              className='w-full h-full object-cover'
                              src={questionResult.videoUrl}
                              preload='metadata'
                              controls
                              disablePictureInPicture={blurVideo}
                          >
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <div className='flex flex-col items-center justify-center text-gray-400'>
                            <VideoIcons className='w-16 h-16 mb-4' />
                            <p className='text-lg font-medium'>
                              Video Not Available
                            </p>
                            <p className='text-sm'>
                              The video response could not be loaded.
                            </p>
                          </div>
                        ) }
                      </div>
                    </div>
                  </div>

                  { /* Right Column: Metadata & Transcript */ }
                  <div className='sm:col-span-2 flex flex-col space-y-4'>
                    { /* Video Metadata */ }
                    <div className='grid grid-cols-1 gap-3'>
                      <div className='bg-gray-50 rounded-none border border-gray-200 p-3'>
                        <div className='flex items-center gap-2 mb-1'>
                          <div className='w-2 h-2 rounded-none bg-blue-500'></div>
                          <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                            Total Points: { questionResult.score || 0 }
                          </span>
                        </div>
                      </div>
                    </div>

                    { /* Response Text (if available) */ }
                    { questionResult.userAnswer && (
                      <div className='bg-gray-50 rounded-none border border-gray-200 p-4 flex-1 flex flex-col min-h-0'>
                        <h4 className='text-xs font-bold text-gray-600 uppercase tracking-wider mb-2'>
                          Transcript
                        </h4>
                        <div className='overflow-y-auto max-h-[150px] sm:max-h-[300px] pr-2 custom-scrollbar'>
                          <p className='text-sm text-gray-700 leading-relaxed whitespace-pre-wrap'>
                            { questionResult.userAnswer }
                          </p>
                        </div>
                      </div>
                    ) }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <>
      <VideoModal />{ ' ' }
      <div className='space-y-6'>
        { /* Response Section */ }
        <div className='rounded-none relative flex flex-col gap-4'>
          { /* Show video for Modern/BEI questions */ }
          { questionResult.questionType === 'Modern' && showVideo ? (
            <div className='relative rounded-none overflow-hidden bg-gray-900'>
              { blurVideo && (
                <div className='absolute inset-0 backdrop-blur-md z-10 flex items-center justify-center'>
                  <div className='bg-white/80 px-4 py-2 rounded-none shadow-lg border border-gray-200'>
                    <p className='text-xs font-bold text-gray-800 uppercase tracking-wider'>
                      Video Blurred
                    </p>
                  </div>
                </div>
              ) }
              <video
                  className='w-full aspect-video'
                  controlsList={blurVideo ? 'nofullscreen' : ''}
                  controls
                  disablePictureInPicture={blurVideo}
                  src={questionResult.videoUrl}
              />
            </div>
          ) : (
            <div className='grid grid-cols-2 gap-4'>
              <div className='bg-white rounded-none p-4 border border-gray-200 shadow-sm'>
                <div className='flex items-center gap-2 mb-3'>
                  <div className='w-5 h-5 rounded-none bg-green-50 border border-green-100 flex items-center justify-center'>
                    <span className='text-[10px] font-bold text-green-600'>
                      S
                    </span>
                  </div>
                  <h3 className='text-xs font-bold text-gray-600 uppercase tracking-wider'>
                    System Analysis
                  </h3>
                </div>
                <p className='text-sm text-gray-700 whitespace-pre-wrap'>
                  { questionResult.systemAnswer }
                </p>
              </div>

              <div className='bg-white rounded-none p-4 border border-gray-200 shadow-sm'>
                <div className='flex items-center gap-2 mb-3'>
                  <div className='w-5 h-5 rounded-none bg-blue-50 border border-blue-100 flex items-center justify-center'>
                    <span className='text-[10px] font-bold text-blue-600'>T</span>
                  </div>
                  <h3 className='text-xs font-bold text-gray-600 uppercase tracking-wider'>
                    Response Transcript
                  </h3>
                </div>
                <p className='text-sm text-gray-700 whitespace-pre-wrap'>
                  { questionResult.userAnswer }
                </p>
              </div>
            </div>
          ) }
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className=' relative flex items-center justify-between px-3 py-1 bg-blue-50 border border-blue-100 rounded-none'>
              <div className='flex items-center gap-3'>
                <div className='flex items-center gap-2'>
                  <div className='w-1.5 h-1.5 rounded-none bg-blue-500'></div>
                  <span className='text-xs font-bold text-gray-800 uppercase tracking-tight'>
                    Total Points: { questionResult.score || 0 }
                  </span>
                </div>
              </div>
              <button
                  onClick={() => setIsVideoModalOpen(true)}
                  className='inline-flex items-center px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-700 bg-white border border-blue-200 rounded-none hover:bg-blue-50 transition-colors'
              >
                <svg
                    className='w-3.5 h-3.5 mr-2'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
                  />
                </svg>
                View Live Response{ ' ' }
              </button>
            </div>
            <div className='bg-amber-50 rounded-none p-1.5 relative border border-amber-200 flex items-center gap-2'>
              <div className='w-7 h-7 rounded-none bg-amber-100 flex items-center justify-center shrink-0'>
                <span className='text-sm'>📸</span>
              </div>
              <div>
                <h4 className='text-[11px] font-bold text-amber-800 uppercase tracking-tight'>
                  Movement Detection
                </h4>
                <p className='text-[10px] text-amber-700 font-medium'>
                  4 instances of significant movement
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className='relative'>
          <div className='mt-6'>
            <div className='space-y-8'>
              { /* Performance Metrics */ }
              <div className='grid md:grid-cols-2 gap-6 grid-cols-1'>
                <div className=''>
                  <h3 className='text-sm font-semibold text-gray-900 mb-3'>
                    Performance Analytics
                  </h3>
                  <div className='grid grid-cols-3 gap-3'>
                    { /* Accuracy Card */ }
                    <div className='bg-white p-2.5 rounded-none border border-gray-200 hover:shadow-sm transition-all'>
                      <div className='flex items-center gap-2 mb-2'>
                        <div className='w-6 h-6 rounded-none bg-blue-50 flex items-center justify-center border border-blue-100'>
                          <svg
                              className='w-3 h-3 text-blue-600'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M5 13l4 4L19 7'
                            />
                          </svg>
                        </div>
                        <h4 className='text-[10px] font-bold text-gray-500 uppercase tracking-wider'>
                          Accuracy
                        </h4>
                      </div>
                      <div className='mt-1'>
                        <p className='text-xl font-bold text-gray-900'>
                          { Math.round(
                            assessmentQuestionResult.similarityScore * 100,
                          ) }
                          %
                        </p>
                        <div className='mt-2 w-full bg-gray-100 rounded-none h-1'>
                          <div
                              className='bg-blue-500 rounded-none h-1 transition-all duration-500'
                              style={{
                                width: `${Math.round(
                                  assessmentQuestionResult.similarityScore * 100,
                                )}%`,
                              }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    { /* Speech Rate Card */ }
                    <div className='bg-white p-2.5 rounded-none border border-gray-200 hover:shadow-sm transition-all'>
                      <div className='flex items-center gap-2 mb-2'>
                        <div className='w-6 h-6 rounded-none bg-green-50 flex items-center justify-center border border-green-100'>
                          <svg
                              className='w-3 h-3 text-green-600'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                          >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z'
                            />
                          </svg>
                        </div>
                        <h4 className='text-[10px] font-bold text-gray-500 uppercase tracking-wider'>
                          Speech Rate
                        </h4>
                      </div>
                      <div className='mt-1'>
                        <div className='flex items-baseline gap-1'>
                          <p className='text-xl font-bold text-gray-900'>
                            { Math.round(assessmentQuestionResult.speechRate) }
                          </p>
                          <span className='text-[10px] text-gray-400 font-bold uppercase'>
                            WPM
                          </span>
                        </div>
                        <div className='mt-2 w-full bg-gray-100 rounded-none h-1'>
                          <div
                              className='bg-green-500 rounded-none h-1 transition-all duration-500'
                              style={{
                                width: `${Math.min(
                                  Math.round(
                                    assessmentQuestionResult.speechRate / 2,
                                  ),
                                  100,
                                )}%`,
                              }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    { /* Emotion Analysis Card */ }
                    { /*<div className='bg-gray-50/50 p-3 rounded-lg border border-gray-200/40 hover:border-gray-300 transition-colors'>*/ }
                    { /*  <div className='flex items-center gap-2 mb-2'>*/ }
                    { /*    <div className='w-7 h-7 rounded-md bg-purple-500/10 flex items-center justify-center'>*/ }
                    { /*      <svg*/ }
                    { /*          className='w-4 h-4 text-purple-600'*/ }
                    { /*          fill='none'*/ }
                    { /*          viewBox='0 0 24 24'*/ }
                    { /*          stroke='currentColor'*/ }
                    { /*      >*/ }
                    { /*        <path*/ }
                    { /*            strokeLinecap='round'*/ }
                    { /*            strokeLinejoin='round'*/ }
                    { /*            strokeWidth={2}*/ }
                    { /*            d='M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'*/ }
                    { /*        />*/ }
                    { /*      </svg>*/ }
                    { /*    </div>*/ }
                    { /*    <h4 className='text-xs font-medium text-gray-600'>*/ }
                    { /*      Emotion*/ }
                    { /*    </h4>*/ }
                    { /*  </div>*/ }
                    { /*  <div className='mt-2'>*/ }
                    { /*    <p className='text-xl font-bold text-gray-900 capitalize truncate'>*/ }
                    { /*      { getHighestFacialEmotionLabel(*/ }
                    { /*        assessmentQuestionResult.facialEmotions,*/ }
                    { /*      ) }*/ }
                    { /*    </p>*/ }
                    { /*    <p className='text-xs text-gray-500 mt-0.5'>*/ }
                    { /*      { Math.round(*/ }
                    { /*        getHighestFacialEmotionPercentage(*/ }
                    { /*          assessmentQuestionResult.facialEmotions,*/ }
                    { /*        ),*/ }
                    { /*      ) }*/ }
                    { /*      % confidence*/ }
                    { /*    </p>*/ }
                    { /*    <div className='mt-2 w-full bg-gray-200/60 rounded-full h-1.5'>*/ }
                    { /*      <div*/ }
                    { /*          className='bg-purple-500 rounded-full h-1.5 transition-all duration-500'*/ }
                    { /*          style={{*/ }
                    { /*            width: `${Math.round(*/ }
                    { /*              getHighestFacialEmotionPercentage(*/ }
                    { /*                assessmentQuestionResult.facialEmotions,*/ }
                    { /*              ),*/ }
                    { /*            )}%`,*/ }
                    { /*          }}*/ }
                    { /*      ></div>*/ }
                    { /*    </div>*/ }
                    { /*  </div>*/ }
                    { /*</div>*/ }
                  </div>
                </div>
                <PermissionGate
                    requiredPermissions={[Permission.ASSESSMENTS_EDIT]}
                >
                  <div className=''>
                    <h3 className='text-xs font-bold text-gray-600 uppercase tracking-wider mb-4'>
                      Review & Assessment
                    </h3>

                    <div className='space-y-4'>
                      <div>
                        <label className='block text-[11px] font-bold text-gray-500 uppercase tracking-tight mb-2'>
                          Personal Notes
                        </label>
                        <TextArea
                            name='personal-notes'
                            customClass='!rounded-none !border-gray-200 focus:!border-blue-400'
                            value={questionResult.personalNotes}
                            onChange={handleChange}
                        />
                      </div>

                      <div className='flex items-center gap-4'>
                        <div className='flex-1'>
                          <CustomSelect
                              name='change'
                              value={
                                questionResult.isCorrect
                                  ? answerSelect[0]
                                  : answerSelect[1]
                              }
                              options={answerSelect}
                              onChange={(option: SelectOptions) =>
                                handleSelectChange(option)
                              }
                          />
                        </div>
                        <Button
                            onClick={saveUpdatedQuestionResult}
                            variant='primary'
                            label='Save Review'
                            className='px-6 !rounded-none'
                        />
                      </div>
                    </div>
                  </div>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
